/**
 * Seeded square particles of a hit burst (W1-06; R§5.3, S§4.3).
 *
 * Per burst (unless the skin hides particles) 4 untextured squares, tinted with the burst colour and the same alpha
 * curve as the sprite:
 *   half  = hitFxScale * noteScale / 44 * (1 - U(0, 0.3))
 *   angle = U(0, 2 pi)
 *   v0    = 2.5 * noteScale * (1 - U(0, 0.1))             world units per second
 *   disp  = v0 * (1 - e^(-6 age)) / 6                      continuous form of Phira's per-frame Euler decay
 * Phira advances particles on real frame time with random jitter; the editor computes them as a pure function of
 * the age with a PRNG seeded by (note id, emission index), so scrubbing reproduces every frame exactly.
 */

import { createRng, mixSeed } from '../../math/rng';
import type { RGBAf } from '../../util/types';
import type { ParticleDraw } from '../types';
import {
  PARTICLE_COUNT,
  PARTICLE_DECAY,
  PARTICLE_HALF_DIVISOR,
  PARTICLE_SIZE_JITTER,
  PARTICLE_SPEED,
  PARTICLE_SPEED_JITTER,
} from './constants';

/** Displacement after `age` seconds for initial speed v0 (continuous exponential decay). */
export function particleDisplacement(v0: number, age: number): number {
  return (v0 * (1 - Math.exp(-PARTICLE_DECAY * age))) / PARTICLE_DECAY;
}

/** Seed of a burst: (note id, emission index within the note). */
export function burstSeed(note: number, seq: number): number {
  return mixSeed(note, seq);
}

/**
 * Appends the burst's particles at UI-space centre (cx, cy) to `out`. `color` already carries the alpha curve.
 */
export function pushParticles(
  seed: number,
  cx: number,
  cy: number,
  age: number,
  hitFxScale: number,
  noteScale: number,
  color: RGBAf,
  out: ParticleDraw[],
): void {
  const rng = createRng(seed);
  for (let k = 0; k < PARTICLE_COUNT; k++) {
    const half = ((hitFxScale * noteScale) / PARTICLE_HALF_DIVISOR) * (1 - PARTICLE_SIZE_JITTER * rng.next());
    const angle = rng.next() * 2 * Math.PI;
    const v0 = PARTICLE_SPEED * noteScale * (1 - PARTICLE_SPEED_JITTER * rng.next());
    const d = particleDisplacement(v0, age);
    out.push({ x: cx + Math.cos(angle) * d, y: cy + Math.sin(angle) * d, half, color });
  }
}
